import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { 
  Mail, 
  Eye, 
  EyeOff, 
  AlertCircle, 
  Loader2,
  KeyRound
} from "lucide-react";

interface EmailReAuthDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountId: string;
  emailAddress: string;
  onSuccess?: () => void;
}

export const EmailReAuthDialog = ({ 
  open, 
  onOpenChange, 
  accountId,
  emailAddress,
  onSuccess 
}: EmailReAuthDialogProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!password.trim()) {
      toast({
        title: "Passwort erforderlich",
        description: "Bitte geben Sie Ihr E-Mail-Passwort ein.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      // First test the connection
      const { data: account } = await supabase
        .from('email_accounts')
        .select('imap_host, imap_port, smtp_host, smtp_port')
        .eq('id', accountId)
        .single();

      if (!account) {
        throw new Error('Konto nicht gefunden');
      }

      const { data: testResult, error: testError } = await supabase.functions.invoke('sync-emails', {
        body: { 
          testOnly: true,
          account: {
            email_address: emailAddress,
            imap_host: account.imap_host,
            imap_port: account.imap_port,
            imap_username: emailAddress,
            imap_password: password,
            smtp_host: account.smtp_host,
            smtp_port: account.smtp_port,
            smtp_username: emailAddress,
            smtp_password: password,
          }
        }
      });

      if (testError || !testResult?.success) {
        toast({
          title: "Verbindung fehlgeschlagen",
          description: testResult?.message || "Passwort ungültig. Bitte überprüfen Sie Ihre Eingabe.",
          variant: "destructive",
        });
        return;
      }

      // Update the password in database
      const { error: updateError } = await supabase
        .from('email_accounts')
        .update({
          imap_password: password,
          smtp_password: password,
          is_active: true,
        })
        .eq('id', accountId);

      if (updateError) throw updateError;

      toast({
        title: "Erfolgreich angemeldet",
        description: "Ihr E-Mail-Konto wurde erfolgreich aktualisiert.",
      });

      setPassword("");
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      console.error('Re-auth error:', err);
      toast({
        title: "Fehler",
        description: "Passwort konnte nicht aktualisiert werden.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-red-600 flex items-center justify-center">
              <KeyRound className="h-5 w-5 text-white" />
            </div>
            Passwort aktualisieren
          </DialogTitle>
          <DialogDescription>
            Ihr E-Mail-Passwort hat sich geändert. Bitte geben Sie das neue Passwort ein.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-3 p-3 bg-amber-50 dark:bg-amber-950/50 rounded-lg border border-amber-200 dark:border-amber-800">
          <AlertCircle className="h-5 w-5 text-amber-600 shrink-0" />
          <div className="text-sm">
            <p className="font-medium text-amber-800 dark:text-amber-200">E-Mail-Konto</p>
            <p className="text-amber-700 dark:text-amber-300">{emailAddress}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="reauth-password">Neues Passwort / App-Passwort</Label>
            <div className="relative">
              <Input
                id="reauth-password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              Bei Gmail/Outlook benötigen Sie ein App-Passwort
            </p>
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              Später
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-amber-600 to-red-600 hover:from-amber-700 hover:to-red-700"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Anmelden"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EmailReAuthDialog;
